import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import { toast } from "../../common/components/Toast";
import { apiUrl } from "../../common/api/config";
import AssetSelector from "./AssetSelector";

function AddMemberModal({ open, onClose, onMemberAdded, currentUserRole }) {
  const { t } = useTranslation("members");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("producer");
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [revenueShare, setRevenueShare] = useState("");
  const [loading, setLoading] = useState(false);

  const needsAssets = ["producer", "viewer", "co_producer"].includes(role);

  const getRoleOptions = () => {
    if (currentUserRole === "owner") {
      return [
        { value: "producer", label: t("roles.producer") },
        { value: "owner", label: t("roles.owner") },
        { value: "viewer", label: t("roles.viewer") },
        { value: "co_producer", label: t("roles.co_producer") },
      ];
    }
    return [
      { value: "viewer", label: t("roles.viewer") },
      { value: "co_producer", label: t("roles.co_producer") },
    ];
  };

  const handleClose = () => {
    setName("");
    setEmail("");
    setPhone("");
    setRole("producer");
    setSelectedAssets([]);
    setRevenueShare("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error(t("addMember.nameRequired"));
      return;
    }

    if (needsAssets && selectedAssets.length === 0) {
      toast.error(t("inviteModal.assetsRequired"));
      return;
    }

    if (
      role === "co_producer" &&
      (!revenueShare || parseFloat(revenueShare) <= 0)
    ) {
      toast.error(t("inviteModal.revenueShareRequired"));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(apiUrl + "/projects/create-member/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim() || "",
          phone: phone.trim() || "",
          role,
          asset_ids: needsAssets ? selectedAssets : [],
          ...(role === "co_producer" && {
            revenue_share_percentage: parseFloat(revenueShare),
          }),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to create member");
      }

      toast.success(t("addMember.success"));
      if (onMemberAdded) onMemberAdded(data);
      handleClose();
    } catch (error) {
      toast.error(error.message || t("addMember.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("addMember.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField
            fullWidth
            label={t("addMember.nameLabel")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("addMember.namePlaceholder")}
            required
          />

          <TextField
            fullWidth
            label={t("addMember.emailLabel")}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("addMember.emailPlaceholder")}
            helperText={t("addMember.emailHelper")}
          />

          <TextField
            fullWidth
            label={t("addMember.phoneLabel")}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t("addMember.phonePlaceholder")}
          />

          <FormControl fullWidth>
            <InputLabel>{t("inviteModal.roleLabel")}</InputLabel>
            <Select
              value={role}
              label={t("inviteModal.roleLabel")}
              onChange={(e) => {
                setRole(e.target.value);
                if (e.target.value === "owner") setSelectedAssets([]);
                if (e.target.value !== "co_producer") setRevenueShare("");
              }}
            >
              {getRoleOptions().map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {role === "co_producer" && (
            <TextField
              fullWidth
              label={t("inviteModal.revenueShareLabel")}
              type="number"
              value={revenueShare}
              onChange={(e) => setRevenueShare(e.target.value)}
              placeholder={t("inviteModal.revenueSharePlaceholder")}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0.01, max: 100, step: "0.01" }}
              helperText={t("inviteModal.revenueShareInfo")}
            />
          )}

          {needsAssets && (
            <AssetSelector
              selectedAssets={selectedAssets}
              onChange={setSelectedAssets}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          {t("editModal.cancel")}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {t("addMember.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddMemberModal;
